import { useCallback, useId, useRef, useState } from "react"
import { ArrowLeft, Loader2, Play, Video, X } from "lucide-react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { doc, updateDoc, deleteField } from "firebase/firestore"
import { Button } from "@/components/ui/button"
import { Dialog, DialogPortal } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { db } from "@/lib/firebase"
import {
  STUDIO_WALKTHROUGH_MAX_BYTES,
  parsePersistedStudioWalkthroughVideoUrl,
  uploadStudioWalkthroughVideo,
  validateStudioWalkthroughVideoFile,
} from "@/lib/studioWalkthroughUpload"

/** Same footprint as Follow / Message / studio walkthrough (client) on the artist profile */
const studioWalkthroughProfileButtonClassName =
  "h-14 w-full rounded-[0.6rem] text-base font-medium"

const playDialogOverlayClass = (stacked: boolean) =>
  cn(
    "fixed inset-0 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
    stacked ? "z-[70]" : "z-[60]"
  )

const playDialogContentClass = (stacked: boolean) =>
  cn(
    "fixed left-0 right-0 top-[50%] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto outline-none",
    "max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]",
    stacked ? "z-[70]" : "z-[60]"
  )

function StudioWalkthroughPlayDialog({
  videoUrl,
  open,
  onOpenChange,
  stacked = false,
}: {
  videoUrl: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** When true, stacks above the manage dialog (z-70). */
  stacked?: boolean
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Overlay className={playDialogOverlayClass(stacked)} />
        <DialogPrimitive.Content
          className={playDialogContentClass(stacked)}
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">Studio walkthrough</DialogPrimitive.Title>
          <div className="relative w-full overflow-hidden rounded-[var(--card-radius)] bg-black">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="absolute left-3 top-3 z-20 h-10 w-10 rounded-lg transition-all hover:shadow-lg bg-background dark:bg-card border border-border"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5 text-foreground" />
            </Button>
            <video
              key={videoUrl}
              src={videoUrl}
              controls
              playsInline
              autoPlay
              className="block w-full max-h-[min(85dvh,calc(90vh-1rem))] object-contain"
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}

export function StudioWalkthroughPreviewRow({ videoUrl }: { videoUrl: string }) {
  const [viewerOpen, setViewerOpen] = useState(false)

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        onClick={() => setViewerOpen(true)}
        className={studioWalkthroughProfileButtonClassName}
        aria-label="Play studio walkthrough"
      >
        <Video className="h-5 w-5" />
        Studio Walkthrough
      </Button>

      <StudioWalkthroughPlayDialog videoUrl={videoUrl} open={viewerOpen} onOpenChange={setViewerOpen} />
    </>
  )
}

interface StudioWalkthroughArtistUploadTriggerProps {
  artistId: string
  userUid: string | undefined
  currentVideoUrl: string | undefined
  onVideoUrlUpdated: (url: string | undefined) => void
}

export function StudioWalkthroughArtistUploadTrigger({
  artistId,
  userUid,
  currentVideoUrl,
  onVideoUrlUpdated,
}: StudioWalkthroughArtistUploadTriggerProps) {
  const fileInputId = useId()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const previewVideoRef = useRef<HTMLVideoElement>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [playDialogOpen, setPlayDialogOpen] = useState(false)
  const [pickedFile, setPickedFile] = useState<File | null>(null)
  const [pickError, setPickError] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)

  const persistedWalkthroughUrl = parsePersistedStudioWalkthroughVideoUrl(currentVideoUrl)
  const hasExistingWalkthroughVideo = persistedWalkthroughUrl !== undefined

  const resetPickState = useCallback(() => {
    setPickedFile(null)
    setPickError(null)
    setUploadError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }, [])

  const handleOpenChange = (open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      resetPickState()
      setPlayDialogOpen(false)
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    setUploadError(null)
    if (!file) {
      setPickedFile(null)
      setPickError(null)
      return
    }
    const validation = validateStudioWalkthroughVideoFile(file)
    if (!validation.isValid) {
      setPickedFile(null)
      setPickError(validation.errorMessage)
      return
    }
    setPickError(null)
    setPickedFile(file)
  }

  const handleUpload = async () => {
    if (!pickedFile || !userUid) return
    setIsUploading(true)
    setUploadError(null)
    try {
      const downloadUrl = await uploadStudioWalkthroughVideo(pickedFile, artistId, userUid)
      await updateDoc(doc(db, "artists", artistId), {
        studioWalkthroughVideoUrl: downloadUrl,
      })
      onVideoUrlUpdated(downloadUrl)
      handleOpenChange(false)
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Upload failed.")
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemove = async () => {
    if (!hasExistingWalkthroughVideo) return
    setIsRemoving(true)
    setUploadError(null)
    try {
      await updateDoc(doc(db, "artists", artistId), {
        studioWalkthroughVideoUrl: deleteField(),
      })
      onVideoUrlUpdated(undefined)
      resetPickState()
      setPlayDialogOpen(false)
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Could not remove video.")
    } finally {
      setIsRemoving(false)
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        className={studioWalkthroughProfileButtonClassName}
        onClick={() => setDialogOpen(true)}
        aria-label={
          hasExistingWalkthroughVideo
            ? "Manage studio walkthrough video on your profile"
            : "Upload studio walkthrough video for your profile"
        }
      >
        <Video className="h-5 w-5" />
        {hasExistingWalkthroughVideo ? "Manage walkthrough" : "Upload studio walkthrough"}
      </Button>

      <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
        <DialogPortal>
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-[60] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            )}
          />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-0 right-0 top-[50%] z-[60] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] overflow-y-auto p-0 mx-auto",
              "max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
            )}
          >
            <div className="flex items-center justify-between p-6 pb-4">
              <DialogPrimitive.Title className="text-xl font-semibold text-foreground pr-2">
                Studio walkthrough video
              </DialogPrimitive.Title>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleOpenChange(false)}
                className="shrink-0 p-2 -mr-2"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </div>

            <div className="px-6 pb-6">
              <div className="space-y-4">
                {hasExistingWalkthroughVideo && persistedWalkthroughUrl ? (
                  <>
                    <p className="text-sm text-foreground leading-relaxed text-left">
                      This is live on your profile. Tap play to see the same full-screen preview clients get,
                      or remove it to upload a new walkthrough.
                    </p>

                    <div
                      className={cn(
                        "relative mx-auto w-full max-w-[280px] overflow-hidden rounded-[var(--card-radius)]",
                        "border border-border bg-black shadow-lg ring-1 ring-black/5 dark:ring-white/10"
                      )}
                    >
                      <div className="relative aspect-[9/16] w-full">
                        <video
                          ref={previewVideoRef}
                          src={persistedWalkthroughUrl}
                          muted
                          playsInline
                          preload="metadata"
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            previewVideoRef.current?.pause()
                            setPlayDialogOpen(true)
                          }}
                          className={cn(
                            "absolute inset-0 flex items-center justify-center",
                            "bg-gradient-to-t from-black/50 via-black/20 to-black/30",
                            "transition-colors hover:from-black/55 hover:via-black/25",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                          )}
                          aria-label="Play studio walkthrough"
                        >
                          <span
                            className={cn(
                              "flex h-16 w-16 items-center justify-center rounded-full",
                              "bg-background/95 text-foreground shadow-lg border border-border",
                              "transition-transform hover:scale-105"
                            )}
                          >
                            <Play className="ml-1 h-7 w-7 fill-current" aria-hidden />
                          </span>
                        </button>
                      </div>
                    </div>

                    <StudioWalkthroughPlayDialog
                      videoUrl={persistedWalkthroughUrl}
                      open={playDialogOpen}
                      onOpenChange={setPlayDialogOpen}
                      stacked
                    />

                    {uploadError && (
                      <p className="text-sm text-destructive text-left" role="alert">
                        {uploadError}
                      </p>
                    )}

                    <div className="w-full border border-input rounded-[var(--card-radius)]">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)] border-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        size="lg"
                        disabled={isUploading || isRemoving}
                        aria-busy={isRemoving}
                        aria-label={isRemoving ? "Removing video" : undefined}
                        onClick={() => void handleRemove()}
                      >
                        {isRemoving ? (
                          <Loader2 className="h-6 w-6 animate-spin motion-reduce:animate-none" aria-hidden />
                        ) : (
                          "Remove from profile"
                        )}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-3 text-left">
                      <p className="text-sm text-foreground leading-relaxed">
                        A short tour of your studio helps clients feel comfortable before they book: they see
                        your space, hygiene, and vibe.
                      </p>
                      <p className="text-sm font-medium text-foreground">Tips for the video:</p>
                      <ul className="list-disc space-y-1 pl-4 text-sm text-foreground leading-relaxed">
                        <li>Film vertically on your phone (9:16), steady and well lit.</li>
                        <li>Keep it concise — about 30–90 seconds works well.</li>
                        <li>Show the work area, waiting area, and anything that makes your studio unique.</li>
                      </ul>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Files: MP4, MOV, or WebM — max {STUDIO_WALKTHROUGH_MAX_BYTES / 1024 / 1024} MB.
                      </p>
                    </div>

                    <div className="flex flex-col items-stretch gap-3 pt-2">
                      <input
                        ref={fileInputRef}
                        id={fileInputId}
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
                        className="sr-only"
                        onChange={handleFileChange}
                        disabled={isUploading}
                      />
                      <div className="w-full border border-input rounded-[var(--card-radius)]">
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)] border-0"
                          size="lg"
                          disabled={isUploading}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {pickedFile ? "Choose a different file" : "Choose video file"}
                        </Button>
                      </div>

                      {pickError && (
                        <p className="text-sm text-destructive" role="alert">
                          {pickError}
                        </p>
                      )}

                      {pickedFile && !pickError && (
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          Selected: {pickedFile.name} ({(pickedFile.size / (1024 * 1024)).toFixed(1)} MB)
                        </p>
                      )}

                      {uploadError && (
                        <p className="text-sm text-destructive" role="alert">
                          {uploadError}
                        </p>
                      )}

                      <Button
                        type="button"
                        className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)]"
                        size="lg"
                        disabled={!pickedFile || !!pickError || isUploading || !userUid}
                        aria-busy={isUploading}
                        aria-label={isUploading ? "Uploading video" : undefined}
                        onClick={() => void handleUpload()}
                      >
                        {isUploading ? (
                          <Loader2 className="h-6 w-6 animate-spin motion-reduce:animate-none" aria-hidden />
                        ) : (
                          "Upload"
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </>
  )
}
